import { dequal } from 'dequal';
import {
  InterviewQuestions,
  ProjectCreationValues,
  ProjectProps,
  ScreeningQuestion,
} from '@/types';
import { isInterviewQuestionsEmpty } from './questionGroupHelpers';

/**
 * The top-level ProjectCreationValues keys that participate in save-payload
 * diffing. `snippets` is intentionally excluded (TELV1-490 architect review,
 * Point 1 normalization table) — it is not part of the server diff surface.
 */
export const PROJECT_DIFF_FIELDS: (keyof ProjectCreationValues)[] = [
  'type_of_interview',
  'interviewer_name',
  'company_name',
  'interview_goal',
  'interview_audience',
  'tone_of_voice',
  'language',
  'allow_language_selection',
  'privacy_policy',
  'analysis_language',
  'theme_id',
  'opening_message',
  'respondents_max',
  'interview_questions',
  'screening_questions',
];

// Fields whose form representation is `'' | undefined` while the server can
// return `null | undefined` — all three are semantically "unset".
const EMPTY_STRING_EQUIVALENT_KEYS = new Set<keyof ProjectCreationValues>([
  'interviewer_name',
  'company_name',
  'interview_goal',
  'interview_audience',
  'tone_of_voice',
  'language',
  'privacy_policy',
  'theme_id',
  'opening_message',
]);

/**
 * Strips `media` keys (recursively) from an interview-questions tree before
 * comparison. `media` can hold a runtime `File` object (uploaded via the
 * separate media endpoint, not this JSON payload) — `File` instances are
 * non-serializable and irrelevant to whether the *text* content changed.
 */
const stripMedia = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripMedia);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => key !== 'media')
        .map(([key, v]) => [key, stripMedia(v)]),
    );
  }
  return value;
};

// Screening questions may carry keys explicitly set to `undefined` (e.g. a
// NUMERIC question's `responses` key) depending on which code path built the
// object. `dequal` treats `{ a: undefined }` and `{}` as different (distinct
// key counts), so normalize both sides by dropping undefined-valued keys
// before comparing.
const stripUndefinedKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripUndefinedKeys);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([key, v]) => [key, stripUndefinedKeys(v)]),
    );
  }
  return value;
};

/**
 * Normalizes a single ProjectCreationValues field so that semantically
 * equivalent-but-differently-shaped form/server values compare equal. See
 * the TELV1-490 architect review normalization table for the source of each
 * rule.
 */
export const normalizeProjectFieldValue = (
  key: keyof ProjectCreationValues,
  rawValue: unknown,
): unknown => {
  if (EMPTY_STRING_EQUIVALENT_KEYS.has(key)) {
    return rawValue === null || rawValue === undefined || rawValue === '' ? '' : rawValue;
  }

  switch (key) {
    case 'type_of_interview':
      return Array.isArray(rawValue) && rawValue.length === 0 ? undefined : rawValue;

    case 'allow_language_selection':
      // Server `false` and unset both render as "no selection" in the UI;
      // treat them as equal. Real `true` still compares distinctly.
      return rawValue === false || rawValue === undefined ? null : rawValue;

    case 'analysis_language':
      return !rawValue ? 'English' : rawValue;

    case 'respondents_max':
      return rawValue === 0 || rawValue === undefined ? null : rawValue;

    case 'interview_questions': {
      const questions = rawValue as InterviewQuestions | undefined;
      return isInterviewQuestionsEmpty(questions) ? null : stripMedia(questions);
    }

    case 'screening_questions': {
      const list = (rawValue as ScreeningQuestion[] | undefined) || [];
      return list.length === 0 ? undefined : stripUndefinedKeys(list);
    }

    default:
      return rawValue;
  }
};

/**
 * True when `formValue` and `serverValue` are semantically the same for the
 * given field, after normalization. Used both for save-payload diffing and
 * for the Save-button enable signal, so the two never disagree (TELV1-490
 * architect review, Point 1a).
 */
export const isProjectFieldUnchanged = (
  key: keyof ProjectCreationValues,
  formValue: unknown,
  serverValue: unknown,
): boolean =>
  dequal(normalizeProjectFieldValue(key, formValue), normalizeProjectFieldValue(key, serverValue));

/**
 * Value-based diff between the current form values and the last-known
 * server state, across the fixed `PROJECT_DIFF_FIELDS` key set. Deliberately
 * ignores `dirtyFields` — React Hook Form's dirty tracking can miss real
 * changes (programmatic `setValue` without `shouldDirty`, drag-reorder,
 * TELV1-400) or flag changes that aren't semantically real ('' vs null,
 * false vs null, etc — see the normalization table above). Comparing actual
 * values against the actual server state is the only signal that can't lie
 * in either direction.
 */
export const getChangedProjectFields = (
  formValues: ProjectCreationValues,
  serverValues: ProjectProps | undefined,
): Partial<ProjectCreationValues> => {
  const changed: Record<string, unknown> = {};

  PROJECT_DIFF_FIELDS.forEach((key) => {
    const formValue = formValues[key];
    const serverValue = serverValues?.[key as keyof ProjectProps];
    if (!isProjectFieldUnchanged(key, formValue, serverValue)) {
      changed[key] = formValue;
    }
  });

  return changed as Partial<ProjectCreationValues>;
};

/**
 * Media (question stimuli) uploads must only ever accompany a FULL save
 * (TELV1-490 PR review MAJOR finding). Pending media (`fileData`) is not
 * part of the value-diff surface — `normalizeProjectFieldValue`'s
 * `interview_questions` case strips `media` before comparing — so a partial
 * save (some other field failed validation) has no way to represent "media
 * upload still pending" in what it persists. If a partial save both
 * uploaded the media AND cleared the ready-for-upload flag, a later full
 * save would never re-attempt the upload, silently losing it. Gating both
 * the upload and the flag-clear behind `isFullSave` keeps the flag pending
 * until a save actually completes the whole form, so the next full save
 * always re-attempts it.
 */
export const shouldUploadMediaOnSave = (
  isFullSave: boolean,
  isMediaReadyForUpload: boolean,
): boolean => isFullSave && isMediaReadyForUpload;
